/**
 * A record's whole detail page -- Detail, Form, Files, Visibility and every other tab -- in a drawer that rises from
 * the bottom, without leaving the page.
 *
 * ED, 2026-11-04: "when a user clicks on an Object ID, instead of showing it in the right side drawer can you show the
 * entire detail page (detail tab, form tab, etc) in a bottom drawer that comes up 50% of the page?", then "replace the
 * side drawer everywhere". It replaced a right-hand drawer (2026-10-28) that drew a read-only summary of its own;
 * this one mounts ObjectDetail itself, embedded, so the drawer can do exactly what the page does -- edit, revise,
 * submit -- with the page's own guards, and there is no second rendering of a record to drift from the first.
 *
 * WHAT IT ADDS OVER THE PAGE: a handle on its top edge to make it taller or shorter (the height is remembered in this
 * browser, because 50% of a laptop screen is tight for the Form tab), "Full record" to leave for the page, and a back
 * arrow once a link inside it has opened another record here.
 *
 * THE PAGE ITSELF is EmbeddedRecord, shared with the right section of Folders.
 */
import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router";
import {Box, Drawer, IconButton, Tooltip, Typography} from "@mui/material";
import {ArrowBackOutlined, CloseOutlined, OpenInNewOutlined} from "@mui/icons-material";

import {Blue3, Grey2, Grey5, White} from "../../theme/styles";
import EmbeddedRecord from "./EmbeddedRecord";

type Props = {
    /** The REVISION to show. Null closes the drawer. */
    revisionUSID: string | null;
    sessionContext: any;
    onClose: () => void;
};

const HEIGHT_KEY = "recordDrawer.heightFraction";
const DEFAULT_FRACTION = 0.5, MIN_FRACTION = 0.25, MAX_FRACTION = 0.95;

function rememberedFraction(): number {
    try {
        const stored = Number(localStorage.getItem(HEIGHT_KEY));
        return stored >= MIN_FRACTION && stored <= MAX_FRACTION ? stored : DEFAULT_FRACTION;
    } catch {
        return DEFAULT_FRACTION;
    }
}

export default function RecordDrawer({revisionUSID, sessionContext, onClose}: Props) {
    const navigate = useNavigate();
    // WHAT IS SHOWN, and the records followed to get here. The caller owns which record was clicked; a link followed
    // inside the drawer, or Back, changes what is shown without the caller having to know.
    const [shown, setShown] = useState<string | null>(revisionUSID);
    const [trail, setTrail] = useState<string[]>([]);
    const [fraction, setFraction] = useState<number>(rememberedFraction);
    const latestFraction = useRef(fraction);
    latestFraction.current = fraction;

    // A NEW CLICK FROM THE PAGE: while open, the record on show goes on the trail; when closed, the trail starts over.
    const previousProp = useRef<string | null>(revisionUSID);
    useEffect(() => {
        const previous = previousProp.current;
        previousProp.current = revisionUSID;
        if (!revisionUSID) { setTrail([]); setShown(null); return; }
        if (previous && previous !== revisionUSID) setTrail(t => [...t, previous]);
        setShown(revisionUSID);
    }, [revisionUSID]);

    const follow = (next: string) => {
        if (!next || next === shown) return;
        if (shown) setTrail(t => [...t, shown]);
        setShown(next);
    };
    const back = () => {
        const previous = trail[trail.length - 1];
        if (!previous) return;
        setTrail(t => t.slice(0, -1));
        setShown(previous);
    };
    const close = () => { setTrail([]); onClose(); };

    // THE HANDLE: drag the top edge; the height is a share of the window, so it means the same after a resize.
    const startDrag = (event: React.PointerEvent) => {
        event.preventDefault();
        const move = (e: PointerEvent) =>
            setFraction(Math.min(MAX_FRACTION, Math.max(MIN_FRACTION, 1 - e.clientY / window.innerHeight)));
        const up = () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);
            try { localStorage.setItem(HEIGHT_KEY, String(latestFraction.current)); } catch { /* a private window keeps no height */ }
        };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
    };

    return (
        <Drawer anchor="bottom" open={Boolean(revisionUSID)} onClose={close}
                slotProps={{paper: {sx: {height: `${Math.round(fraction * 100)}vh`, backgroundColor: White,
                                         borderTopLeftRadius: 10, borderTopRightRadius: 10, overflow: "hidden",
                                         display: "flex", flexDirection: "column"}}}}>
            {/* THE HANDLE, and the drawer's own bar: back, full record, close. */}
            <Box onPointerDown={startDrag} role="separator" aria-label="Drag to resize"
                 sx={{height: 14, flexShrink: 0, cursor: "ns-resize", display: "flex", alignItems: "center",
                      justifyContent: "center", touchAction: "none"}}>
                <Box sx={{width: 44, height: 4, borderRadius: 2, backgroundColor: Grey2}}/>
            </Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 2, pb: 0.75, flexShrink: 0,
                      borderBottom: `1px solid ${Grey2}`}}>
                {trail.length > 0 && (
                    <Tooltip title="Back to the record before">
                        <IconButton size="small" onClick={back} aria-label="Back"><ArrowBackOutlined sx={{fontSize: 18}}/></IconButton>
                    </Tooltip>
                )}
                <Box sx={{flex: 1}}/>
                <Typography sx={{fontSize: 12.5, color: Blue3, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px"}}
                            onClick={() => { if (shown) { close(); navigate(`/object/${shown}`); } }}>
                    Full record <OpenInNewOutlined sx={{fontSize: 14}}/>
                </Typography>
                <IconButton size="small" onClick={close} aria-label="Close"><CloseOutlined sx={{fontSize: 18}}/></IconButton>
            </Box>

            <Box sx={{flex: 1, minHeight: 0, display: "flex", flexDirection: "column"}}>
                {shown ? (
                    <EmbeddedRecord revisionUSID={shown} sessionContext={sessionContext} onOpenRecord={follow}/>
                ) : (
                    <Typography sx={{fontSize: 13, color: Grey5, p: 3}}>Nothing is open.</Typography>
                )}
            </Box>
        </Drawer>
    );
}
